/**
 * SportMemoryPage — /memoire (V12.x)
 *
 * Page patrimoniale premium : « Mémoire du Sport Polynésien »
 * Sections dans l'ordre demandé :
 *   A. Hero
 *   B. Les incontournables
 *   C. La frise du temps
 *   D. Explorer par univers
 *   E. Les livres de notre histoire
 *   F. Archives audiovisuelles (grille + filtres)
 *   G. Les grands rendez-vous
 *   H. Figures de la mémoire
 *   I. Contribuer
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { adminMailto } from "@/lib/adminContact";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Video, BookOpen, FileText, ExternalLink, Search, Library, Archive as ArchiveIcon,
  Sparkles, Calendar, Users as UsersIcon, Flame, ArrowRight, Newspaper, Landmark,
  Trophy, Compass, Waves, Wind, ScrollText, FolderArchive, ChevronRight, MapPin, Globe,
} from "lucide-react";
import { FocusJeuxPacifique } from "@/components/FocusJeuxPacifique";

// ------- Constantes visuelles / éditoriales -------

const CONTENT_TYPE_BADGE = {
  ARCHIVE_VIDEO: { label: "ARCHIVE VIDÉO", icon: Video, color: "bg-red-100 text-red-800 border-red-200" },
  ARCHIVE_AUDIO: { label: "ARCHIVE AUDIO", icon: Video, color: "bg-red-100 text-red-800 border-red-200" },
  ARCHIVE_PHOTO: { label: "ARCHIVE PHOTO", icon: ArchiveIcon, color: "bg-orange-100 text-orange-800 border-orange-200" },
  ARTICLE_HISTORIQUE: { label: "ARTICLE HISTORIQUE", icon: Newspaper, color: "bg-blue-100 text-blue-800 border-blue-200" },
  LIVRE: { label: "OUVRAGE", icon: BookOpen, color: "bg-amber-100 text-amber-900 border-amber-200" },
  THESE: { label: "THÈSE", icon: ScrollText, color: "bg-purple-100 text-purple-800 border-purple-200" },
  ETUDE: { label: "ÉTUDE", icon: ScrollText, color: "bg-purple-100 text-purple-800 border-purple-200" },
  RESULTATS: { label: "RÉSULTATS", icon: Trophy, color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  PALMARES: { label: "PALMARÈS", icon: Trophy, color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  TEMOIGNAGE: { label: "TÉMOIGNAGE", icon: UsersIcon, color: "bg-cyan-100 text-cyan-800 border-cyan-200" },
  EVENEMENT: { label: "ÉVÉNEMENT", icon: Flame, color: "bg-rose-100 text-rose-800 border-rose-200" },
  PORTRAIT: { label: "PORTRAIT", icon: UsersIcon, color: "bg-cyan-100 text-cyan-800 border-cyan-200" },
  DOCUMENT_OFFICIEL: { label: "DOCUMENT OFFICIEL", icon: FileText, color: "bg-slate-100 text-slate-800 border-slate-200" },
  CATALOGUE_ARCHIVES: { label: "CATALOGUE D'ARCHIVES", icon: FolderArchive, color: "bg-slate-100 text-slate-800 border-slate-200" },
  HISTOIRE_INSTITUTIONNELLE: { label: "SOURCE INSTITUTIONNELLE", icon: Landmark, color: "bg-indigo-100 text-indigo-800 border-indigo-200" },
};

const CTA_BY_MEDIA_TYPE = {
  VIDEO: { label: "Voir la vidéo", icon: Video },
  PDF: { label: "Consulter le document", icon: FileText },
  BOOK: { label: "Voir la référence", icon: BookOpen },
  WEBPAGE: { label: "Lire la source", icon: ExternalLink },
  ARCHIVE_CATALOG: { label: "Explorer les archives", icon: FolderArchive },
  NONE: { label: "Voir la fiche", icon: ArrowRight },
};

const UNIVERSES = [
  { key: "Tū'aro Mā'ohi", label: "Tū'aro Mā'ohi & sports ancestraux", icon: Flame },
  { key: "Va'a", label: "Va'a & navigation", icon: Waves },
  { key: "Surf", label: "Surf & sports de glisse", icon: Wind },
  { key: "Jeux du Pacifique", label: "Jeux du Pacifique", icon: Trophy, byEvent: true },
  { key: "Jeux olympiques", label: "Jeux olympiques", icon: Trophy, byEvent: true },
  { key: "Football", label: "Football", icon: Compass },
  { key: "Beach Soccer", label: "Tiki Toa / Beach Soccer", icon: Compass },
  { key: "combat", label: "Sports de combat", icon: Sparkles, byTag: true },
  { key: "Athlétisme", label: "Athlétisme", icon: Sparkles },
  { key: "Cyclisme", label: "Cyclisme", icon: Sparkles },
  { key: "Rugby", label: "Rugby", icon: Sparkles },
  { key: "Sport scolaire", label: "Sport scolaire", icon: Sparkles },
  { key: "Pêche sous-marine", label: "Pêche sous-marine", icon: Waves },
  { key: "LIVRE", label: "Livres & recherches", icon: BookOpen, byContentType: true },
  { key: "ARCHIVE_VIDEO", label: "Archives audiovisuelles", icon: Video, byContentType: true },
  { key: "figures", label: "Grandes figures du sport", icon: UsersIcon, isFigures: true },
];

const COLLECTIONS = [
  { key: "Jeux du Pacifique", label: "Jeux du Pacifique", filter: { event: "Jeux du Pacifique" } },
  { key: "tahiti-1971", label: "Tahiti 1971", filter: { q: "1971", event: "Jeux du Pacifique" } },
  { key: "tahiti-1995", label: "Tahiti 1995", filter: { q: "1995", event: "Jeux du Pacifique" } },
  { key: "tahiti-2027", label: "Tahiti 2027", filter: { q: "2027" } },
  { key: "hawaiki-nui", label: "Hawaiki Nui Va'a", filter: { event: "Hawaiki Nui Va'a" } },
  { key: "tiki-toa", label: "Tiki Toa", filter: { sport: "Beach Soccer" } },
  { key: "teahupoo", label: "Teahupo'o 2024", filter: { q: "Teahupo" } },
  { key: "heiva", label: "Heiva / Tū'aro Mā'ohi", filter: { sport: "Tū'aro Mā'ohi" } },
];

// ------- Composant carte réutilisable -------

function MemoryCard({ item, size = "default" }) {
  const badge = CONTENT_TYPE_BADGE[item.content_type] || CONTENT_TYPE_BADGE.ARTICLE_HISTORIQUE;
  const cta = CTA_BY_MEDIA_TYPE[item.media_type] || CTA_BY_MEDIA_TYPE.NONE;
  const primaryUrl = item.media_url || item.document_url || item.source_url;
  const BadgeIcon = badge.icon;
  const CtaIcon = cta.icon;
  const yearLabel = item.year_start
    ? (item.year_end && item.year_end !== item.year_start ? `${item.year_start}–${item.year_end}` : String(item.year_start))
    : null;

  return (
    <Card
      className={`overflow-hidden hover:shadow-lg transition group flex flex-col ${size === "large" ? "h-full" : ""}`}
      data-testid={`sport-memory-card-${item.slug}`}
    >
      <div className="p-4 sm:p-5 flex-1 flex flex-col">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <Badge variant="outline" className={`text-[10px] font-semibold ${badge.color}`}>
            <BadgeIcon className="h-3 w-3 mr-1" />{badge.label}
          </Badge>
          {yearLabel && <Badge variant="outline" className="text-[10px] font-mono">{yearLabel}</Badge>}
          {item.duration && <Badge variant="outline" className="text-[10px] text-muted-foreground">{item.duration}</Badge>}
          {item.link_status === "to_review" && (
            <Badge variant="outline" className="text-[10px] bg-yellow-50 text-yellow-800 border-yellow-200">Lien à vérifier</Badge>
          )}
        </div>
        <h3 className={`font-display font-bold leading-tight ${size === "large" ? "text-lg sm:text-xl" : "text-base"}`}>
          {item.title}
        </h3>
        {item.subtitle && <p className="text-xs sm:text-sm text-muted-foreground italic mt-0.5">{item.subtitle}</p>}
        {item.description_short && (
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed line-clamp-3">{item.description_short}</p>
        )}
        <div className="mt-auto pt-4 space-y-2">
          {item.source_name && (
            <div className="text-[11px] text-muted-foreground">
              <span className="font-semibold">Source :</span> {item.source_name}
            </div>
          )}
          <div className="flex items-center gap-2">
            {primaryUrl ? (
              <Button size="sm" asChild data-testid={`sport-memory-cta-${item.slug}`}
                className="bg-[hsl(var(--aito-lagoon))] hover:bg-[hsl(var(--aito-lagoon))]/90 text-white">
                <a href={primaryUrl} target="_blank" rel="noopener noreferrer">
                  <CtaIcon className="h-3.5 w-3.5 mr-1.5" /> {cta.label}
                  <ExternalLink className="h-3 w-3 ml-1 opacity-70" />
                </a>
              </Button>
            ) : null}
            <Button size="sm" variant="outline" asChild>
              <Link to={`/patrimoine/${item.slug}`}>Fiche <ChevronRight className="h-3 w-3 ml-1" /></Link>
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

// ------- Page principale -------

export default function SportMemoryPage() {
  const [items, setItems] = useState([]);
  const [facets, setFacets] = useState({ sports: [], events: [], content_types: [], people: [] });
  const [timeline, setTimeline] = useState({ milestones: [], periods: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterCT, setFilterCT] = useState("__all__");
  const [filterSport, setFilterSport] = useState("__all__");
  const [mode, setMode] = useState("frise"); // frise | sports | bibliotheque

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/sport-memory", { params: { limit: 200 } }),
      api.get("/sport-memory/facets"),
      api.get("/sport-memory/timeline"),
    ])
      .then(([lst, fac, tl]) => {
        setItems(lst.data.items || []);
        setFacets(fac.data || {});
        setTimeline(tl.data || { milestones: [], periods: [] });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = items;
    if (q.trim()) {
      const qq = q.toLowerCase();
      list = list.filter((it) =>
        (it.title || "").toLowerCase().includes(qq) ||
        (it.description_short || "").toLowerCase().includes(qq) ||
        (it.tags || []).some((t) => (t || "").toLowerCase().includes(qq)) ||
        (it.people || []).some((p) => (p || "").toLowerCase().includes(qq))
      );
    }
    if (filterCT !== "__all__") list = list.filter((it) => it.content_type === filterCT);
    if (filterSport !== "__all__") list = list.filter((it) =>
      it.sport === filterSport || (it.sports || []).includes(filterSport)
    );
    return list;
  }, [items, q, filterCT, filterSport]);

  const featured = useMemo(
    () => items.filter((it) => it.featured).sort((a, b) => (b.editorial_priority || 0) - (a.editorial_priority || 0)).slice(0, 6),
    [items]
  );
  const books = useMemo(
    () => items.filter((it) => it.content_type === "LIVRE").sort((a, b) => (b.editorial_priority || 0) - (a.editorial_priority || 0)),
    [items]
  );
  const videos = useMemo(
    () => filtered.filter((it) => it.media_type === "VIDEO").sort((a, b) => (a.year_start || 9999) - (b.year_start || 9999)),
    [filtered]
  );
  const figures = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      for (const p of it.people || []) {
        if (!map.has(p)) map.set(p, { name: p, items: [] });
        map.get(p).items.push(it);
      }
    }
    return Array.from(map.values()).sort((a, b) => b.items.length - a.items.length);
  }, [items]);

  // Palmarès Patrick Pons en priorité pour la section Livres
  const booksOrdered = useMemo(() => {
    const pons = books.filter((b) => (b.author || "").includes("Pons"));
    const others = books.filter((b) => !(b.author || "").includes("Pons"));
    return [...pons, ...others];
  }, [books]);

  return (
    <div className="pb-16" data-testid="sport-memory-page">
      {/* ============ A. HERO ============ */}
      <section className="relative overflow-hidden">
        <img
          src="/branding/memoire-banniere.png"
          alt="Mémoire du sport à Tahiti"
          className="w-full h-auto max-h-[420px] object-contain mx-auto block bg-white"
          data-testid="memoire-hero-banner"
        />
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
          <Badge className="bg-[hsl(var(--aito-lagoon))]/10 text-[hsl(var(--aito-lagoon))] border-[hsl(var(--aito-lagoon))]/30 mb-3">Mémoire du sport polynésien</Badge>
          <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-foreground">
            Mémoire du Sport Polynésien
          </h1>
          <p className="mt-3 text-base sm:text-lg text-muted-foreground leading-snug max-w-3xl">
            Des premiers ‘aito aux champions d'aujourd'hui, retrouver les événements, images, ouvrages et témoignages qui ont construit l'histoire sportive de la Polynésie.
          </p>
          <p className="mt-3 text-sm text-muted-foreground max-w-3xl leading-relaxed">
            AITO.PF rassemble, référence et met en perspective les archives, ouvrages, résultats, témoignages et documents qui racontent l'histoire du sport en Polynésie française.
            Les contenus restent hébergés et crédités par leurs institutions, auteurs et ayants droit respectifs.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <Button size="lg" className="bg-[hsl(var(--aito-lagoon))] hover:bg-[hsl(var(--aito-lagoon))]/90 text-white" onClick={() => document.getElementById("frise")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-cta-timeline">
              <Calendar className="h-4 w-4 mr-2" /> Explorer la chronologie
            </Button>
            <Button size="lg" variant="outline" onClick={() => document.getElementById("archives")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-cta-videos">
              <Video className="h-4 w-4 mr-2" /> Voir les archives vidéo
            </Button>
            <Button size="lg" variant="outline" onClick={() => document.getElementById("livres")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-cta-library">
              <Library className="h-4 w-4 mr-2" /> Parcourir la bibliothèque
            </Button>
            <Button size="lg" variant="outline" onClick={() => document.getElementById("contribuer")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-cta-contribute">
              <Sparkles className="h-4 w-4 mr-2" /> Contribuer à la mémoire
            </Button>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">

        {/* ============ HISTOIRE DES JP (visuel de synthèse) — PRIORITÉ ============ */}
        <section className="pt-8" data-testid="section-histoire-jp">
          <div className="mb-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="bg-[hsl(var(--aito-gold))]/15 text-[hsl(var(--aito-gold-strong,var(--aito-gold)))] border-[hsl(var(--aito-gold))]/40 font-semibold">À la Une</Badge>
              <h2 className="font-display font-bold text-2xl sm:text-3xl">Histoire des Jeux du Pacifique &amp; parcours de Tahiti</h2>
            </div>
            <p className="text-sm text-muted-foreground mt-1">De 1963 à 2027 — repères clés, éditions et médailles d'or.</p>
          </div>
          <Card className="p-2 sm:p-3 overflow-hidden bg-white border-[hsl(var(--aito-gold))]/30">
            <img
              src="/branding/histoire-jp-visuel.png"
              alt="Histoire des Jeux du Pacifique et parcours de Tahiti de 1963 à 2027 — repères, éditions et médailles"
              className="w-full h-auto rounded"
              data-testid="histoire-jp-image"
            />
          </Card>
          <p className="mt-2 text-[11px] text-muted-foreground text-center italic">
            Visuel de synthèse AITO.PF · sources : Pacific Games Council, COPF, Tahiti VOD, archives publiques.
          </p>
        </section>

        {/* ============ V13.19 — FOCUS JEUX & MINI-JEUX DU PACIFIQUE (archives historiques) ============ */}
        <FocusJeuxPacifique />

        {/* ============ B. LES INCONTOURNABLES ============ */}
        {featured.length > 0 && (
          <section className="pt-12" data-testid="section-incontournables">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-display font-bold text-2xl sm:text-3xl">Les incontournables</h2>
                <p className="text-sm text-muted-foreground mt-1">Des grands rendez-vous et des ouvrages fondateurs à connaître.</p>
              </div>
              <Badge variant="outline" className="font-mono text-xs">{featured.length}</Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {featured.map((it) => <MemoryCard key={it.id} item={it} size="large" />)}
            </div>
          </section>
        )}

        {/* ============ C. LA FRISE DU TEMPS ============ */}
        <section id="frise" className="pt-14" data-testid="section-frise">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-display font-bold text-2xl sm:text-3xl flex items-center gap-2">
                <Calendar className="h-6 w-6 text-[hsl(var(--aito-lagoon))]" /> La frise du temps
              </h2>
              <p className="text-sm text-muted-foreground mt-1">Une chronologie éditoriale du sport polynésien.</p>
            </div>
          </div>
          <ol className="relative border-l-2 border-[hsl(var(--aito-lagoon))]/25 pl-5 space-y-6">
            {(timeline.periods || []).filter((p) => p.items.length > 0).map((p) => (
              <li key={p.key}>
                <div className="absolute -left-1.5 mt-1 h-3 w-3 rounded-full bg-[hsl(var(--aito-lagoon))] shadow-md" />
                <div className="text-xs uppercase tracking-wide font-semibold text-[hsl(var(--aito-lagoon))]">{p.label}</div>
                <div className="mt-2 space-y-1.5">
                  {(timeline.milestones || []).filter((m) => m.year >= p.min && m.year <= p.max).map((m, i) => (
                    <div key={`${p.key}-m-${i}`} className="flex items-start gap-2 text-sm">
                      <span className="inline-block font-mono font-bold text-[hsl(var(--aito-lagoon))] w-14 shrink-0">{m.year}</span>
                      <span className="text-muted-foreground leading-snug">{m.label}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {p.items.slice(0, 6).map((it) => <MemoryCard key={it.id} item={it} />)}
                </div>
                {p.items.length > 6 && (
                  <div className="mt-2 text-xs text-muted-foreground italic">+ {p.items.length - 6} autres fiches sur cette période</div>
                )}
              </li>
            ))}
          </ol>
        </section>

        {/* ============ D. EXPLORER PAR UNIVERS ============ */}
        <section className="pt-16" data-testid="section-univers">
          <div className="mb-4">
            <h2 className="font-display font-bold text-2xl sm:text-3xl flex items-center gap-2">
              <Compass className="h-6 w-6 text-[hsl(var(--aito-gold))]" /> Explorer par univers
            </h2>
            <p className="text-sm text-muted-foreground mt-1">Choisir un thème pour filtrer la bibliothèque.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {UNIVERSES.map((u) => {
              const Icon = u.icon;
              const onClick = () => {
                setMode("bibliotheque");
                if (u.isFigures) { document.getElementById("figures")?.scrollIntoView({ behavior: "smooth" }); return; }
                if (u.byContentType) setFilterCT(u.key);
                else setFilterCT("__all__");
                if (u.byEvent) setQ(u.key);
                else if (u.byTag) setQ(u.key);
                else setFilterSport(u.byContentType ? "__all__" : u.key);
                document.getElementById("bibliotheque")?.scrollIntoView({ behavior: "smooth" });
              };
              return (
                <button
                  key={u.key}
                  onClick={onClick}
                  className="group text-left p-4 rounded-lg border bg-card hover:border-[hsl(var(--aito-gold))] hover:bg-[hsl(var(--aito-gold))]/5 transition"
                  data-testid={`universe-${u.key}`}
                >
                  <Icon className="h-5 w-5 text-[hsl(var(--aito-lagoon))] mb-2" />
                  <div className="font-semibold text-sm leading-tight group-hover:text-[hsl(var(--aito-lagoon))]">{u.label}</div>
                </button>
              );
            })}
          </div>
        </section>

        {/* ============ E. LES LIVRES DE NOTRE HISTOIRE ============ */}
        <section id="livres" className="pt-16" data-testid="section-livres">
          <div className="mb-5">
            <h2 className="font-display font-bold text-2xl sm:text-3xl flex items-center gap-2">
              <BookOpen className="h-6 w-6 text-amber-700" /> Les livres de notre histoire
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Ouvrages fondateurs. Patrick Pons ouvre la marche — un travail patrimonial de plusieurs décennies.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {booksOrdered.map((it) => <MemoryCard key={it.id} item={it} />)}
          </div>
        </section>

        {/* ============ F. ARCHIVES AUDIOVISUELLES ============ */}
        <section id="archives" className="pt-16" data-testid="section-archives">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <div>
              <h2 className="font-display font-bold text-2xl sm:text-3xl flex items-center gap-2">
                <Video className="h-6 w-6 text-red-600" /> Archives audiovisuelles
              </h2>
              <p className="text-sm text-muted-foreground mt-1">Filmées, documentaires, portraits — hébergées par les fonds patrimoniaux d'origine.</p>
            </div>
            <Badge variant="outline" className="font-mono text-xs">{videos.length}</Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {videos.map((it) => <MemoryCard key={it.id} item={it} />)}
          </div>
        </section>

        {/* ============ G. LES GRANDS RENDEZ-VOUS ============ */}
        <section className="pt-16" data-testid="section-collections">
          <div className="mb-4">
            <h2 className="font-display font-bold text-2xl sm:text-3xl flex items-center gap-2">
              <Trophy className="h-6 w-6 text-[hsl(var(--aito-gold))]" /> Les grands rendez-vous
            </h2>
            <p className="text-sm text-muted-foreground mt-1">Sélections éditoriales par événement.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {COLLECTIONS.map((c) => {
              const count = items.filter((it) => {
                if (c.filter.event) return (it.event || "").toLowerCase().includes(c.filter.event.toLowerCase());
                if (c.filter.sport) return (it.sport || "") === c.filter.sport;
                if (c.filter.q) return (it.title || "").toLowerCase().includes(c.filter.q.toLowerCase()) || (it.event || "").toLowerCase().includes(c.filter.q.toLowerCase());
                return false;
              }).length;
              return (
                <button
                  key={c.key}
                  onClick={() => {
                    setMode("bibliotheque");
                    setQ(c.filter.q || "");
                    setFilterSport(c.filter.sport || "__all__");
                    document.getElementById("bibliotheque")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="text-left p-4 rounded-lg border bg-gradient-to-br from-[hsl(var(--aito-lagoon))]/5 to-[hsl(var(--aito-gold))]/5 hover:from-[hsl(var(--aito-lagoon))]/10 hover:to-[hsl(var(--aito-gold))]/10 transition"
                  data-testid={`collection-${c.key}`}
                >
                  <div className="font-display font-bold text-base">{c.label}</div>
                  <div className="text-xs text-muted-foreground mt-1">{count} fiche{count > 1 ? "s" : ""}</div>
                </button>
              );
            })}
          </div>
        </section>

        {/* ============ H. FIGURES DE LA MÉMOIRE ============ */}
        {figures.length > 0 && (
          <section id="figures" className="pt-16" data-testid="section-figures">
            <div className="mb-4">
              <h2 className="font-display font-bold text-2xl sm:text-3xl flex items-center gap-2">
                <UsersIcon className="h-6 w-6 text-[hsl(var(--aito-lagoon))]" /> Figures de la mémoire
              </h2>
              <p className="text-sm text-muted-foreground mt-1">Personnes citées dans les archives et ressources répertoriées.</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {figures.map((f) => (
                <button
                  key={f.name}
                  onClick={() => { setQ(f.name); setMode("bibliotheque"); document.getElementById("bibliotheque")?.scrollIntoView({ behavior: "smooth" }); }}
                  className="text-left p-4 rounded-lg border bg-card hover:border-[hsl(var(--aito-gold))] transition"
                  data-testid={`figure-${f.name}`}
                >
                  <div className="flex items-center gap-2">
                    <div className="h-9 w-9 rounded-full bg-[hsl(var(--aito-lagoon))]/15 flex items-center justify-center">
                      <UsersIcon className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{f.name}</div>
                      <div className="text-[11px] text-muted-foreground">{f.items.length} document{f.items.length > 1 ? "s" : ""}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* ============ BIBLIOTHÈQUE (recherche/filtres) ============ */}
        <section id="bibliotheque" className="pt-16" data-testid="section-bibliotheque">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <div>
              <h2 className="font-display font-bold text-2xl sm:text-3xl flex items-center gap-2">
                <Library className="h-6 w-6 text-[hsl(var(--aito-lagoon))]" /> Bibliothèque
              </h2>
              <p className="text-sm text-muted-foreground mt-1">Recherche plein texte, filtres discipline & type.</p>
            </div>
            <Badge variant="outline" className="font-mono text-xs">{filtered.length}/{items.length}</Badge>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un titre, une figure, un événement…" className="pl-9" data-testid="library-search" />
            </div>
            <Select value={filterCT} onValueChange={setFilterCT}>
              <SelectTrigger className="sm:w-56" data-testid="library-filter-content-type"><SelectValue placeholder="Type de contenu" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tous types</SelectItem>
                {(facets.content_types || []).map((c) => (
                  <SelectItem key={c.key} value={c.key}>
                    {CONTENT_TYPE_BADGE[c.key]?.label || c.key} ({c.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterSport} onValueChange={setFilterSport}>
              <SelectTrigger className="sm:w-52" data-testid="library-filter-sport"><SelectValue placeholder="Discipline" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Toutes disciplines</SelectItem>
                {(facets.sports || []).map((s) => (
                  <SelectItem key={s.key} value={s.key}>{s.key} ({s.count})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(q || filterCT !== "__all__" || filterSport !== "__all__") && (
              <Button variant="ghost" size="sm" onClick={() => { setQ(""); setFilterCT("__all__"); setFilterSport("__all__"); }} data-testid="library-clear">
                Effacer
              </Button>
            )}
          </div>
          {loading ? (
            <div className="text-center text-sm text-muted-foreground py-8">Chargement…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-8 border rounded-lg">Aucun résultat pour ces filtres.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((it) => <MemoryCard key={it.id} item={it} />)}
            </div>
          )}
        </section>

        {/* ============ I. CONTRIBUER ============ */}
        <section id="contribuer" className="pt-16 pb-6" data-testid="section-contribuer">
          <Card className="p-6 sm:p-8 bg-gradient-to-br from-[hsl(var(--aito-lagoon))]/8 to-[hsl(var(--aito-gold))]/8 border-[hsl(var(--aito-gold))]/30">
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <div className="h-12 w-12 rounded-full bg-[hsl(var(--aito-lagoon))]/15 flex items-center justify-center shrink-0">
                <Sparkles className="h-6 w-6 text-[hsl(var(--aito-lagoon))]" />
              </div>
              <div className="flex-1">
                <h3 className="font-display font-bold text-xl sm:text-2xl">Contribuer à la mémoire</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed max-w-2xl">
                  Une photo ancienne, un résultat, une affiche, un programme, un témoignage ou l'histoire d'un champion dort peut-être dans vos archives.
                  Contribuez à préserver la mémoire sportive polynésienne.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button asChild data-testid="contribute-cta">
                    <a href={adminMailto({ subject: "Proposer une archive à la Mémoire du Sport Polynésien" })}>Proposer une archive</a>
                  </Button>
                  <Button asChild variant="outline">
                    <Link to="/memoire/vivante">Voir la Mémoire vivante d'AITO.PF</Link>
                  </Button>
                </div>
              </div>
            </div>
          </Card>
          <p className="mt-4 text-[11px] text-muted-foreground text-center">
            Ressources externes référencées par AITO.PF à des fins documentaires et patrimoniales. Toutes les archives et ouvrages restent la propriété de leurs auteurs, éditeurs et fonds d'origine.
          </p>
        </section>
      </div>
    </div>
  );
}
